import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  EmailEventListItem,
  allEmailEventsQuery,
} from "@/app/EmailEvents/GraphQL/allEmailEventsQuery";
import { emailEventMessages } from "@/app/EmailEvents/Messages/emailTypeMessages";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import {
  EmailEventStatus,
  EmailType,
  QueryAllEmailEventsOrderByColumn,
  SortOrder,
} from "@/gql/graphql";
import { useState } from "react";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type EmailEventColumnId =
  | "recipient"
  | "type"
  | "status"
  | "message_id"
  | "created_at";

const statusColorMap: Record<EmailEventStatus, BadgeColor> = {
  [EmailEventStatus.Accepted]: "zinc",
  [EmailEventStatus.Delivered]: "green",
  [EmailEventStatus.Opened]: "blue",
  [EmailEventStatus.Clicked]: "purple",
  [EmailEventStatus.Unsubscribed]: "orange",
  [EmailEventStatus.Stored]: "zinc",
  [EmailEventStatus.Rejected]: "red",
  [EmailEventStatus.Complained]: "red",
  [EmailEventStatus.PermanentFail]: "red",
  [EmailEventStatus.TemporaryFail]: "yellow",
};

const statusOptions = Object.values(EmailEventStatus).map((status) => ({
  label: emailEventMessages.status[status],
  value: status,
}));

const typeOptions = Object.values(EmailType).map((type) => ({
  label: emailEventMessages.type[type],
  value: type,
}));

export function EmailEventTable() {
  "use no memo";

  const [statusFilter, setStatusFilter] = useState<EmailEventStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<EmailType[]>([]);

  const columns: DataTableColDef<
    EmailEventListItem,
    any,
    EmailEventColumnId
  >[] = [
    {
      id: "recipient",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Recipient" />
      ),
      accessorFn: (row) => row.recipient,
    },
    {
      id: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      accessorFn: (row) => emailEventMessages.type[row.type],
      cell: DeEmphasisedCell,
    },
    {
      id: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      accessorFn: (row) => row,
      cell: ({ row }) => <StatusCell event={row.original} />,
    },
    {
      id: "message_id",
      header: "Message ID",
      accessorFn: (row) => row.message_id,
      cell: DeEmphasisedCell,
    },
    {
      id: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
  ];

  return (
    <DataTable
      id="email-events"
      initialSorting={[{ id: "created_at", desc: true }]}
      title="Email Events"
      document={allEmailEventsQuery}
      columns={columns}
      accessor={(data) => data.allEmailEvents}
      searchable
      searchPlaceholder="Search by recipient email..."
      getQueryVariables={({ pagination, search, sorting }) => {
        const orderBy = sorting
          .map((sort) => {
            const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

            if (sort.id === "created_at") {
              return {
                order,
                column: QueryAllEmailEventsOrderByColumn.CreatedAt,
              };
            }

            return null;
          })
          .filter(Boolean);

        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          recipient: search ? `%${search}%` : undefined,
          status: statusFilter.length > 0 ? statusFilter : undefined,
          type: typeFilter.length > 0 ? typeFilter : undefined,
          orderBy: orderBy.length
            ? orderBy
            : [
                {
                  order: SortOrder.Desc,
                  column: QueryAllEmailEventsOrderByColumn.CreatedAt,
                },
              ],
        };
      }}
      filters={(table) => (
        <>
          <MultiSelectFilter
            label="Status"
            options={statusOptions}
            selected={statusFilter}
            onChange={(values) => {
              table.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              setStatusFilter(values);
            }}
          />
          <MultiSelectFilter
            label="Type"
            options={typeOptions}
            selected={typeFilter}
            onChange={(values) => {
              table.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              setTypeFilter(values);
            }}
          />
        </>
      )}
    />
  );
}

function StatusCell({ event }: { event: EmailEventListItem }) {
  const badge = (
    <Badge color={statusColorMap[event.status]}>
      {emailEventMessages.status[event.status]}
    </Badge>
  );

  if (!event.failure_reason) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">{badge}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-sm">
        {event.failure_code ? `${event.failure_code} ` : null}
        {event.failure_reason}
      </TooltipContent>
    </Tooltip>
  );
}
